import { FunctionComponent, useMemo, useState, useEffect, useRef, Suspense, lazy, useCallback } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { useFormatMessage } from '../i18n-review/client/Msg';
import { AutocompleteHandle } from '../components/Autocomplete';
import { getIndexName } from '../constants';
import { useAuth } from '../hooks/useAuth';
import { useAppDispatch } from '../redux/hooks';
import { usePageContext } from 'vike-react/usePageContext';
import { FacetsProvider } from '../contexts/FacetsContext';
import { useSearchContext } from '../contexts/SearchContext';
import { SignInPromptModal } from '../components/auth/SignInPromptModal';
import { isToolsPage } from '../utils/routing/route-utils';
import { JOBS_LISTING_SEGMENTS } from '../utils/constants/url-constants';
import { PerformanceProfiler, useRenderLogger } from '../utils/shared/performance-profiler';
// Direct imports (not the barrel): this file also dynamically imports MobileBottomNav/
// MobileMenuModal from components/navigation for code-splitting — statically importing anything
// through the barrel (components/navigation/index.ts) pulls those two into this same chunk too,
// defeating that split (confirmed via an INEFFECTIVE_DYNAMIC_IMPORT build warning).
import { NavigationNavbar } from '../components/navigation/NavigationNavbar';
import { Logo } from '../components/navigation/Logo';
import { LanguageSelector } from '../components/navigation/LanguageSelector';
import Footer from '../components/Footer';
import { ErrorBoundary } from '../design-system/ErrorBoundary';
import { FacetsUpdater } from '../components/facets/FacetsUpdater';
import { LiteShellProvider } from '../contexts/LiteShellContext';
import { DesktopFilterCategoriesMemo } from '../components/search/DesktopFilterCategories';
import {
  cancelIdleScheduleHandle,
  scheduleIdleCallbackWithFallback,
} from '../utils/browser/scheduleIdleCallbackWithFallback';

// Lazy load the content that opens from the top bar (modals, mobile menu)
const MobileBottomNav = lazy(() =>
  import('../components/navigation/MobileBottomNav').then((m) => ({ default: m.MobileBottomNav }))
);
const MobileMenuModal = lazy(() => import('../components/navigation/MobileMenuModal'));

// Lazy load modal components to reduce initial bundle size
const AllFiltersModal = lazy(() => import('../components/AllFiltersModal'));

interface SearchWrapperProps extends React.PropsWithChildren {
  children: React.ReactNode;
  isSearchPage: boolean;
  /** When true, SRP uses minimal header and defers filter bar / mobile nav until interaction (Step 3) */
  useLiteShell?: boolean;
  serverState?: any;
  searchClientOverride?: any;
  disableAuth?: boolean;
  disableChrome?: boolean;
  ssrIsMobile?: boolean;
  ssrIsXl?: boolean;
  initialFacetsData?: any;
  /** When outside Vike tree (e.g. SRP island), pass URL/locale so chrome works without usePageContext(). */
  pathnameOverride?: string;
  urlOriginalOverride?: string;
  localeOverride?: string;
}

export const SearchWrapper: FunctionComponent<SearchWrapperProps> = (props) => {
  const renderCount = 0;

  // Log render summary every 5 renders (client-side only)
  if (false) {}

  const intl: IntlShape = useIntl();

  // Performance logging
  useRenderLogger('SearchWrapper', {
    isSearchPage: props.isSearchPage,
    disableChrome: props.disableChrome,
    disableAuth: props.disableAuth,
  });
  const memoIndexName = useMemo(() => getIndexName(intl.locale), [intl.locale]);

  // Get user from authenticated session (optionally disabled for SSR getServerState)
  const user = (props.disableAuth && typeof window === 'undefined')
    ? null
    : (useAuth().user);
  const [isAllFiltersModalOpen, setIsAllFiltersModalOpen] = useState(false);
  // Which rail section AllFiltersModal currently has open, reported up so the nav's Location
  // button only highlights when the modal is genuinely showing the Location tab (not just
  // whenever the modal happens to be open on some other tab). See AllFiltersModal's
  // onSectionChange and DesktopFilterCategories' isLocationActive.
  const [allFiltersModalSection, setAllFiltersModalSection] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dispatch = useAppDispatch();
  const finlandFilterEnabled = false;

  // Step 3: Lite shell – full filter bar / mobile nav load on interaction or after idle
  const useLiteShell = props.useLiteShell === true && props.isSearchPage;
  const [fullShellLoaded, setFullShellLoaded] = useState(false);
  const requestFullShell = useCallback(() => setFullShellLoaded(true), []);
  useEffect(() => {
    if (!useLiteShell) return;
    const handle = scheduleIdleCallbackWithFallback(() => setFullShellLoaded(true), { timeout: 4000 });
    return () => cancelIdleScheduleHandle(handle);
  }, [useLiteShell]);
  
  useEffect(() => {}, [finlandFilterEnabled]);
  
  // Track hydration state for routing
  const [isHydrated, setIsHydrated] = useState(false);
  
  useEffect(() => { setIsHydrated(true); }, []);

  useEffect(() => {}, [isHydrated]);

  if (false && props.serverState) {}

  if (false) {}

  const pageContext = usePageContext();

  // Compute isSearch from pathname to complement prop (handles client navigations). Use overrides when outside Vike tree (e.g. SRP island).
  const pathname = (pageContext as any)?.urlPathname
    ?? props.pathnameOverride
    ?? (typeof window !== 'undefined' ? window.location.pathname : '/');

  const segs = String(pathname).split('/').filter(Boolean);
  const isLocale = segs[0] && ['en', 'fi', 'sv'].includes(segs[0]);
  // 'jobb' deliberately excluded: it's sv's job-DETAIL segment, not a listing segment
  // (sv's listing segment is 'lediga-jobb'). See url-constants.ts's JOBS_LISTING_SEGMENTS
  // for the 2026-09-02 regression this pattern caused when included here.
  const isJobsKeyword = (s?: string) => !!s && JOBS_LISTING_SEGMENTS.includes(s);
  // Support both with-locale (/en/jobs/...) and locale-stripped (/jobs/...)
  const computedIsSearch =
    segs.length === 0
      || (segs.length === 1 && (isLocale || isJobsKeyword(segs[0])))
      || (isLocale && isJobsKeyword(segs[1]))
      || (!isLocale && isJobsKeyword(segs[0]));
  const isSearch: boolean = !!props.isSearchPage || !!computedIsSearch;

  const content = (
    props.disableChrome ? (
      props.children ?? null
    ) : (
      <ErrorBoundary>
        <SearchWrapperContent
          {...props}
          isSearchPage={isSearch}
          intl={intl}
          isAllFiltersModalOpen={isAllFiltersModalOpen}
          setIsAllFiltersModalOpen={setIsAllFiltersModalOpen}
          allFiltersModalSection={allFiltersModalSection}
          setAllFiltersModalSection={setAllFiltersModalSection}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          user={user}
          useLiteShell={useLiteShell}
          fullShellLoaded={fullShellLoaded}
          requestFullShell={requestFullShell}
        />
      </ErrorBoundary>
    )
  );

  return (
    <PerformanceProfiler id="SearchWrapper">
      <LiteShellProvider
        value={{
          isLiteShell: useLiteShell,
          fullShellLoaded: useLiteShell ? fullShellLoaded : undefined,
          requestFullShell: useLiteShell ? requestFullShell : undefined,
        }}
      >
        <FacetsProvider initialFacetsData={props.initialFacetsData}>
          <FacetsUpdater />
          <div className="min-h-screen bg-jc-page w-full max-w-full">
            <ErrorBoundary>
              {content}
            </ErrorBoundary>
          </div>
        </FacetsProvider>
      </LiteShellProvider>
    </PerformanceProfiler>
  );
};

interface SearchWrapperContentProps extends SearchWrapperProps {
  intl: IntlShape;
  isAllFiltersModalOpen: boolean;
  setIsAllFiltersModalOpen: (value: boolean) => void;
  allFiltersModalSection: string | null;
  setAllFiltersModalSection: (value: string | null) => void;
  isMobileMenuOpen?: boolean;
  setIsMobileMenuOpen?: (open: boolean) => void;
  user: any | null | undefined;
  useLiteShell?: boolean;
  fullShellLoaded?: boolean;
  requestFullShell?: () => void;
}

const SearchWrapperContent: FunctionComponent<SearchWrapperContentProps> = (props) => {
  const formatMessage = useFormatMessage();
  const pageContext = usePageContext();
  // Map view renders its own fixed-to-viewport layout (see MapView's height calc) and no longer
  // renders the SRP footer below it (see SrpView.tsx) — so the page content should exactly match
  // the viewport with no scroll, instead of the `overflow-y-auto` list/grid views need. Without
  // this, a trackpad/touch drag meant for panning the map could scroll the whole page instead.
  const { viewMode } = useSearchContext();
  const isMapView = props.isSearchPage && viewMode === 'map';
  const urlOriginal = (pageContext as any)?.urlOriginal ?? props.urlOriginalOverride ?? (typeof window !== 'undefined' ? window.location.href : '/');
  const fullUrlContent = urlOriginal;
  const pathnameForClient = fullUrlContent.startsWith("http") ? new URL(fullUrlContent).pathname : fullUrlContent;
  // SSR/CSR compatible pathname
  const url = urlOriginal;
  const pathname = url.startsWith("http") ? new URL(url).pathname : url;
  const isLocationHubPage =
    pathname.includes('/locations') ||
    pathname.includes('/sijainnit') ||
    pathname.includes('/platser');
  const isProfilePage = pathname.includes('/profile');
  const isDashboardOrLocationHubPage =
    pathname.includes('/backoffice/dashboard') || isLocationHubPage || isProfilePage;
  const onToolsPage = isToolsPage(pathname);
  
  // Parse current URL to get location if present
  // We no longer need the custom hook; URL creation uses the router in InstantSearch

  // Refs for autocomplete components (desktop and mobile)
  const desktopAutocompleteRef = useRef<AutocompleteHandle>(null);
  const mobileAutocompleteRef = useRef<AutocompleteHandle>(null);

  // Helper to close all autocomplete instances
  const closeAllAutocompletes = useCallback(() => {
    desktopAutocompleteRef.current?.close();
    mobileAutocompleteRef.current?.close();
  }, []);

  // Callback to close filter modals when autocomplete opens
  const handleAutocompleteOpen = useCallback(() => {
    if (props.isAllFiltersModalOpen) {
      props.setIsAllFiltersModalOpen?.(false);
    }
  }, [props.isAllFiltersModalOpen, props.setIsAllFiltersModalOpen]);

  const clearSearch = async (event?: React.MouseEvent<HTMLAnchorElement>): Promise<void> => {
    // Prevent default so we control the destination (home, not a cleared search URL).
    if (event) {
      event.preventDefault();
    }
    const locale = ((pageContext as any)?.locale ?? (props as any).localeOverride ?? 'en') as 'en' | 'fi' | 'sv';
    // Hard-navigate to the locale home (landing page). The landing page uses
    // injectScriptsAt:null, so a full server fetch is required — no Vike navigate().
    // React state is discarded on the reload, so no explicit reset is needed.
    window.location.assign(locale === 'en' ? '/' : `/${locale}`);
  };

  // Render logging for debugging
  useRenderLogger('SearchWrapperContent', {
    isSearchPage: props.isSearchPage,
    isAllFiltersModalOpen: props.isAllFiltersModalOpen,
    isMobileMenuOpen: props.isMobileMenuOpen,
  });

  // Add '/' shortcut for search button on non-search pages
  useEffect(() => {
    if (props.isSearchPage) return;
    const handler = (e: KeyboardEvent) => {
      if (
        e.key === '/' &&
        !(document.activeElement instanceof HTMLInputElement) &&
        !(document.activeElement instanceof HTMLTextAreaElement) &&
        !(document.activeElement instanceof HTMLSelectElement)
      ) {
        e.preventDefault();
        const btn = document.getElementById('search-shortcut-btn');
        if (btn) {
          (btn as HTMLElement).focus();
          (btn as HTMLElement).click();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [props.isSearchPage]);

  return (
    <div
      className={
        (isLocationHubPage
          ? 'relative flex flex-col min-h-screen'
          : 'relative flex flex-col h-screen mobile-viewport-height overflow-hidden lg:overflow-visible') +
        ' bg-jc-page'
      }
    >
      <SignInPromptModal />
      {/* Lazy load AllFiltersModal only when needed */}
      {props.isAllFiltersModalOpen && (
        <Suspense fallback={null}>
          <AllFiltersModal
            isOpen={true}
            onRequestClose={() => props.setIsAllFiltersModalOpen(false)}
            onCloseComplete={() => {
              if (props.isAllFiltersModalOpen) props.setIsAllFiltersModalOpen(false);
              props.setAllFiltersModalSection(null);
            }}
            onSectionChange={props.setAllFiltersModalSection}
          />
        </Suspense>
      )}
      {/* Top Header - Navigation and Search Controls */}
      <header role="banner">
        {!props.isSearchPage && (
          <NavigationNavbar
            intl={props.intl}
            isMobileMenuOpen={props.isMobileMenuOpen}
            setIsMobileMenuOpen={props.setIsMobileMenuOpen}
            clearSearch={clearSearch}
            user={props.user}
          />
        )}
        {/* SRP desktop top bar: logo, autocomplete, filter buttons, hamburger – static, no skeleton; only the content that opens (modals, menu) is lazy-loaded */}
        {props.isSearchPage && (
          <DesktopFilterCategoriesMemo
            intl={props.intl}
            isMobileMenuOpen={props.isMobileMenuOpen}
            setIsMobileMenuOpen={props.setIsMobileMenuOpen}
            isAllFiltersModalOpen={props.isAllFiltersModalOpen}
            setIsAllFiltersModalOpen={props.setIsAllFiltersModalOpen}
            allFiltersModalSection={props.allFiltersModalSection}
            clearSearch={clearSearch}
            user={props.user}
            closeAutocomplete={closeAllAutocompletes}
            autocompleteRef={desktopAutocompleteRef}
            onAutocompleteOpen={handleAutocompleteOpen}
          />
        )}
      </header>
      {/* Content wrapper - on desktop, contains menu and content, menu shrinks content width */}
      <div className="flex-1 flex lg:flex-row lg:relative lg:overflow-hidden min-h-0">
        {/* Mobile Menu Modal — always mounted (same as non-SRP); portaled to body inside the component */}
        {props.isMobileMenuOpen !== undefined && (
          <Suspense fallback={null}>
            <MobileMenuModal
              isOpen={props.isMobileMenuOpen}
              onRequestClose={() => props.setIsMobileMenuOpen?.(false)}
              onCloseComplete={() => {
                if (props.isMobileMenuOpen) props.setIsMobileMenuOpen?.(false);
              }}
              fullUrlContent={fullUrlContent}
              intl={props.intl}
            />
          </Suspense>
        )}
        {/* Main content - flex-1 allows it to compress when keyboard appears; min-w-0 so SRP container can shrink for container queries */}
        {/* On lg+ screens, width shrinks when menu is open (via margin-right) */}
        <div
          data-srp-scroll-region={isLocationHubPage ? undefined : ''}
          className={`flex-1 min-w-0 bg-jc-page ${
            isLocationHubPage
              ? 'overflow-x-hidden lg:overflow-x-visible overflow-y-visible min-h-screen'
              : isMapView
              ? 'overflow-hidden min-h-0'
              : 'overflow-x-hidden overflow-y-auto min-h-0 overscroll-y-contain touch-pan-y [-webkit-overflow-scrolling:touch]'
          } transition-transform duration-300 ease-out ${
            props.isMobileMenuOpen ? 'lg:mr-[420px]' : 'lg:mr-0'
          } ${
            onToolsPage
              ? ''
              : isDashboardOrLocationHubPage
              ? 'pt-1 sm:pt-1 md:pt-2 pb-20 lg:pb-0'
              : props.isSearchPage
              ? // Below lg, map view drops this padding entirely: the near-fullscreen mobile map
                // (see MapView.tsx's height calc) is measured against the sticky header's real
                // height plus the flex column's own gap, and this padding isn't part of that
                // measurement — leaving it in just opened a dead gap between the header and the
                // map that had no purpose once the toolbar row above it collapsed to nothing.
                // lg+ keeps it: desktop's map view still has the toolbar row visible above it,
                // same as list view, so the same breathing room applies there.
                isMapView
                ? 'pt-0 sm:pt-0 md:pt-0 lg:pt-2 pb-20 lg:pb-0'
                : 'pt-1 sm:pt-1 md:pt-2 pb-20 lg:pb-0'
              : 'pt-1 sm:pt-1 md:pt-2 pb-20 lg:pb-0 sm:px-0 md:px-2 lg:px-8'
          }`}
        >
          {props.children}
          {/* Site footer is chrome-owned for non-SRP pages; the SRP renders its own inside SrpView. */}
          {!props.isSearchPage && <Footer />}
        </div>
      </div>
      {/* Bottom Header for mobile - lazy loaded; skip in lite shell until fullShellLoaded */}
      {props.isSearchPage && !(props.useLiteShell && !props.fullShellLoaded) && (
        <header className="lg:hidden flex-shrink-0" style={{ position: 'relative', zIndex: 60 }} role="banner">
          <Suspense fallback={<div className="h-14 bg-jc-elevated" />}>
            <MobileBottomNav
              formatMessage={formatMessage}
              isSearchPage={props.isSearchPage}
              isAllFiltersModalOpen={props.isAllFiltersModalOpen}
              setIsAllFiltersModalOpen={props.setIsAllFiltersModalOpen}
              isMobileMenuOpen={props.isMobileMenuOpen}
              setIsMobileMenuOpen={props.setIsMobileMenuOpen}
              mobileAutocompleteRef={mobileAutocompleteRef}
              closeAllAutocompletes={closeAllAutocompletes}
              handleAutocompleteOpen={handleAutocompleteOpen}
            />
          </Suspense>
        </header>
      )}
    </div>
  );
};

